import { useIntl } from 'react-intl';
import { Box, Chip, Tooltip } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import { getOrderCellState, OrderAllocation } from './orderPresentation';
import { TableCell } from './Table.css';

interface OrderCellProps extends OrderAllocation {
  // An unsaved client or project change: the order is resolved on save.
  pending?: boolean;
  // The assignment the save would link, if the date's availability names
  // one for the row's client.
  pendingOrderNumber?: string | null;
  // False for rows that are not saved here (custom projects).
  unresolvedIsPending?: boolean;
}

// One "Work order" value per entry: the order number (hours and days in the
// tooltip), a warning when an order is missing and "—" when none applies.
export const OrderCellContent = ({
  pending = false,
  unresolvedIsPending = true,
  pendingOrderNumber,
  ...allocation
}: OrderCellProps) => {
  const intl = useIntl();
  const state = getOrderCellState(
    allocation,
    pending,
    unresolvedIsPending,
    pendingOrderNumber,
  );
  const text = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id }, values);

  const content = (() => {
    switch (state.kind) {
      case 'pending':
        return state.orderNumber
          ? text('ORDER_PRESENTATION.PENDING_WITH_NUMBER', {
              number: state.orderNumber,
            })
          : text('ORDER_PRESENTATION.PENDING');
      case 'historical':
        return text('ORDER_PRESENTATION.HISTORICAL');
      case 'linked':
        return (
          <Tooltip
            title={text('ORDER_PRESENTATION.MEASURES', {
              hours: allocation.orderHours ?? '—',
              days: allocation.orderDays ?? '—',
            })}
          >
            <Chip size="small" variant="outlined" label={state.orderNumber} />
          </Tooltip>
        );
      case 'missing':
        return (
          <Tooltip title={text('ORDER_PRESENTATION.MISSING_TOOLTIP')}>
            <Chip
              size="small"
              color="secondary"
              icon={<WarningIcon fontSize="small" />}
              label={text('ORDER_PRESENTATION.MISSING')}
            />
          </Tooltip>
        );
      default:
        return (
          <Tooltip
            title={text(
              state.kind === 'notRequired'
                ? 'ORDER_PRESENTATION.NOT_REQUIRED_TOOLTIP'
                : 'ORDER_PRESENTATION.NOT_IN_SCOPE_TOOLTIP',
            )}
          >
            <span>—</span>
          </Tooltip>
        );
    }
  })();

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      gridGap={4}
    >
      {content}
      {allocation.needsCorrection && !pending && (
        <Tooltip title={text('ORDER_PRESENTATION.NEEDS_CORRECTION_TOOLTIP')}>
          <WarningIcon
            color="error"
            fontSize="small"
            aria-label={text('ORDER_PRESENTATION.NEEDS_CORRECTION')}
            aria-hidden={false}
            role="img"
          />
        </Tooltip>
      )}
    </Box>
  );
};

const OrderCell = (props: OrderCellProps) => (
  <TableCell align="center">
    <OrderCellContent {...props} />
  </TableCell>
);

export default OrderCell;
